import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductInfo = ({ product, onAddToCart, onAddToWishlist, isInWishlist }) => {
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    await onAddToCart(product, quantity);
    setTimeout(() => setIsAddingToCart(false), 1000);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="StarHalf" size={16} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={16} className="text-border" />
      );
    }

    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Product Title */}
      <div>
        <h1 className="font-heading font-heading-bold text-2xl lg:text-3xl text-text-primary mb-2">
          {product.name}
        </h1>
        <p className="text-text-secondary font-body">{product.brand}</p>
      </div>

      {/* Rating and Reviews */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-1">
          {renderStars(product.rating)}
        </div>
        <span className="font-data font-data-medium text-text-primary">
          {product.rating}
        </span>
        <span className="text-text-secondary">
          ({product.reviewCount} reviews)
        </span>
      </div>

      {/* Price */}
      <div className="space-y-2">
        <div className="flex items-center space-x-3">
          <span className="font-heading font-heading-bold text-3xl text-primary">
            ${product.price.toFixed(2)}
          </span>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="font-data text-lg text-text-secondary line-through">
              ${product.originalPrice.toFixed(2)}
            </span>
          )}
          {product.discount && (
            <span className="bg-accent text-accent-foreground px-2 py-1 rounded-input text-sm font-data font-data-medium">
              {product.discount}% OFF
            </span>
          )}
        </div>
        <p className="text-text-secondary font-body">
          {product.unit} | ${(product.price / parseFloat(product.unit.split(' ')[0])).toFixed(2)} per {product.unit.split(' ')[1]}
        </p>
      </div>

      {/* Stock Status */}
      <div className="flex items-center space-x-2">
        <Icon 
          name={product.inStock ? "CheckCircle" : "XCircle"} 
          size={20} 
          className={product.inStock ? "text-success" : "text-error"} 
        />
        <span className={`font-body-semibold ${product.inStock ? "text-success" : "text-error"}`}>
          {product.inStock ? "In Stock" : "Out of Stock"}
        </span>
        {product.inStock && product.stockCount && (
          <span className="text-text-secondary">
            ({product.stockCount} available)
          </span>
        )}
      </div>

      {/* Quantity Selector */}
      {product.inStock && (
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <span className="font-body-semibold text-text-primary">Quantity:</span>
            <div className="flex items-center border border-border rounded-input">
              <Button
                variant="ghost"
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                className="p-2 rounded-none border-r border-border"
                iconName="Minus"
                iconSize={16}
              />
              <span className="px-4 py-2 font-data font-data-medium min-w-[3rem] text-center">
                {quantity}
              </span>
              <Button
                variant="ghost"
                onClick={incrementQuantity}
                className="p-2 rounded-none border-l border-border"
                iconName="Plus"
                iconSize={16}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <Button
              variant="primary"
              onClick={handleAddToCart}
              loading={isAddingToCart}
              className="flex-1"
              iconName="ShoppingCart"
              iconPosition="left"
            >
              {isAddingToCart ? "Adding..." : "Add to Cart"}
            </Button>
            <Button
              variant="outline"
              onClick={() => onAddToWishlist(product)}
              iconName={isInWishlist ? "Heart" : "Heart"}
              className={isInWishlist ? "text-error border-error" : ""}
            >
              {isInWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
            </Button>
          </div>
        </div>
      )}

      {/* Share */}
      <div className="flex items-center space-x-4 pt-4 border-t border-border">
        <span className="font-body-semibold text-text-primary">Share:</span>
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            className="p-2"
            iconName="Share2"
            iconSize={20}
          />
          <Button
            variant="ghost"
            className="p-2"
            iconName="Facebook"
            iconSize={20}
          />
          <Button
            variant="ghost"
            className="p-2"
            iconName="Twitter"
            iconSize={20}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;